import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { removeLoadingKey } from "../helpers/common";

const TAB_IDS = ["product", "bundle", "packaging", "supplies"];

const createEmptyFilters = () => ({
    search: "",
    productName: "all",
    productGroupId: "all",
    status: "all",
    category: "all",
    supplier: "all",
});

const ensureArray = (value) => (Array.isArray(value) ? value : []);

export const BomUiLoadingKeys = {
    fetchProductUi: "FETCH_PRODUCT_BOM_UI",
    saveProductBom: "SAVE_PRODUCT_BOM",
    saveProductBomBulk: "SAVE_PRODUCT_BOM_BULK",
    deleteAllProductBom: "DELETE_ALL_PRODUCT_BOM",
    fetchComponentLibrary: "FETCH_BOM_COMPONENT_LIBRARY",
    saveComponentLibraryItem: "SAVE_BOM_COMPONENT_LIBRARY_ITEM",
    deleteComponentLibraryItem: "DELETE_BOM_COMPONENT_LIBRARY_ITEM",
    getSupplies: "GET_BOM_SUPPLIES",
    fetchPackagingUi: "FETCH_PACKAGING_BOM_UI",
    fetchBundleUi: "FETCH_BUNDLE_BOM_UI",
    upsertSupply: "UPSERT_BOM_SUPPLY",
    deleteSupply: "DELETE_BOM_SUPPLY",
    createSupplierCategory: "CREATE_SUPPLIER_CATEGORY",
    deleteSupplierCategory: "DELETE_SUPPLIER_CATEGORY",
    searchProducts: "SEARCH_BOM_PRODUCTS",
    searchProductGroups: "SEARCH_BOM_PRODUCT_GROUPS",
    savePackagingBom: "SAVE_PACKAGING_BOM",
    deletePackagingBom: "DELETE_PACKAGING_BOM",
    savePackagingInstructions: "SAVE_PACKAGING_INSTRUCTIONS",
    deletePackagingInstructions: "DELETE_PACKAGING_INSTRUCTIONS",
    getBomChangeLog: "GET_BOM_CHANGE_LOG",
    rollbackBomChanges: "ROLLBACK_BOM_CHANGES",
};

const initialState = {
    activeTab: "product",
    settingsOpen: false,
    settingsSection: "components",
    settingsExpandedBoxGroupIds: [],
    settingsExpandedInstructionGroupIds: [],
    changeHistoryOpen: false,
    bomChangeLog: [],
    bomChangeLogPagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 100,
        isTheLastPage: true,
    },
    loading: false,
    loadingKeys: [],
    error: null,
    filtersByTab: {
        product: createEmptyFilters(),
        bundle: createEmptyFilters(),
        packaging: createEmptyFilters(),
        supplies: createEmptyFilters(),
    },
    selectedIdsByTab: {
        product: [],
        bundle: [],
        packaging: [],
        supplies: [],
    },
    expandedIdsByTab: {
        product: [],
        bundle: [],
        packaging: [],
    },
    data: {
        products: [],
        bundles: [],
        packaging: [],
        supplies: [],
        componentLibrary: [],
        boxPresets: [],
        /** Saved packaging BOMs per product group (from GET packaging-ui). */
        packagingBoxPresetOptions: [],
        /** Saved packaging instructions per product group (from GET packaging-ui). */
        packagingInstructionPresetOptions: [],
        instructionPresets: [],
        supplyCategories: [],
    },
};

export const upsertBomSupply = createAsyncThunk(
    "bomUi/upsertBomSupply",
    async (payload, { rejectWithValue, dispatch }) => {
        try {
            const { data } = await axios.post("/api/v1/bill-of-materials/supplies", payload);
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const deleteBomSupply = createAsyncThunk(
    "bomUi/deleteBomSupply",
    async ({ productId }, { rejectWithValue, dispatch }) => {
        try {
            const { data } = await axios.delete(`/api/v1/bill-of-materials/supplies/${productId}`);
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const createSupplierCategory = createAsyncThunk(
    "bomUi/createSupplierCategory",
    async ({ name }, { rejectWithValue, dispatch }) => {
        try {
            const { data } = await axios.post("/api/v1/bill-of-materials/supplier-categories", { name });
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const deleteSupplierCategory = createAsyncThunk(
    "bomUi/deleteSupplierCategory",
    async ({ id }, { rejectWithValue, dispatch }) => {
        try {
            const { data } = await axios.delete(`/api/v1/bill-of-materials/supplier-categories/${id}`);
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const searchBomProducts = createAsyncThunk(
    "bomUi/searchBomProducts",
    async (
        { search, limit = 12, excludeExistingProductBom = true, excludeExistingSupplyProduct = false, excludeByBOMType = false },
        { rejectWithValue }
    ) => {
        try {
            const { data } = await axios.get("/api/v1/bill-of-materials/product-search-create", {
                params: { search, limit, excludeExistingProductBom, excludeExistingSupplyProduct, excludeByBOMType },
            });
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const searchBomProductGroups = createAsyncThunk(
    "bomUi/searchBomProductGroups",
    async ({ search, limit = 12 }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get("/api/v1/bill-of-materials/product-group-search", {
                params: { search, limit },
            });
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const savePackagingBom = createAsyncThunk(
    "bomUi/savePackagingBom",
    async (body, { rejectWithValue }) => {
        try {
            const { data } = await axios.post("/api/v1/bill-of-materials/packaging-bom", body);
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const deletePackagingBom = createAsyncThunk(
    "bomUi/deletePackagingBom",
    async ({ group_ids }, { rejectWithValue }) => {
        try {
            const { data } = await axios.delete("/api/v1/bill-of-materials/packaging-bom", {
                data: { group_ids },
            });
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const savePackagingInstructions = createAsyncThunk(
    "bomUi/savePackagingInstructions",
    async (body, { rejectWithValue }) => {
        try {
            const { data } = await axios.post("/api/v1/bill-of-materials/packaging-instructions", body);
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const getBomChangeLog = createAsyncThunk(
    "bomUi/getBomChangeLog",
    async ({ page = 1, limit = 100, sku, search, bomTab } = {}, { rejectWithValue }) => {
        try {
            const { data } = await axios.get("/api/v1/bill-of-materials/change-log", {
                params: { page, limit, sku, search, bomTab },
            });
            return data;
        } catch (error) {
            return rejectWithValue(
                error?.response?.data?.error || error?.response?.data?.errors || error.message
            );
        }
    }
);

export const rollbackBomChanges = createAsyncThunk(
    "bomUi/rollbackBomChanges",
    async ({ changeLogId, batchId, note }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post("/api/v1/bill-of-materials/change-log/rollback", {
                changeLogId,
                batchId,
                note,
            });
            return data;
        } catch (error) {
            return rejectWithValue(
                error?.response?.data?.error || error?.response?.data?.errors || error.message
            );
        }
    }
);

export const deletePackagingInstructions = createAsyncThunk(
    "bomUi/deletePackagingInstructions",
    async ({ group_ids }, { rejectWithValue }) => {
        try {
            const { data } = await axios.delete("/api/v1/bill-of-materials/packaging-instructions", {
                data: { group_ids },
            });
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const fetchPackagingBomUi = createAsyncThunk(
    "bomUi/fetchPackagingBomUi",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get("/api/v1/bill-of-materials/packaging-ui");
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const fetchBundleBomUi = createAsyncThunk(
    "bomUi/fetchBundleBomUi",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get("/api/v1/bill-of-materials/bundle-ui");
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const fetchSuppliesBomUi = createAsyncThunk(
    "bomUi/fetchSuppliesBomUi",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get("/api/v1/bill-of-materials/supplies-ui");
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const fetchProductBomUi = createAsyncThunk(
    "bomUi/fetchProductBomUi",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get("/api/v1/bill-of-materials/product-ui");
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const saveProductBom = createAsyncThunk(
    "bomUi/saveProductBom",
    async ({ productId, components }, { rejectWithValue }) => {
        try {
            const id = String(productId || "").trim();
            if (!id) {
                return rejectWithValue("Invalid product id");
            }
            const { data } = await axios.put(`/api/v1/bill-of-materials/product-ui/${id}`, {
                components,
            });
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const saveProductBomBulk = createAsyncThunk(
    "bomUi/saveProductBomBulk",
    async ({ productIds, components, mode = "replace" }, { rejectWithValue }) => {
        try {
            const ids = Array.from(
                new Set((Array.isArray(productIds) ? productIds : []).map((id) => String(id || "").trim()).filter(Boolean))
            );
            if (!ids.length) {
                return rejectWithValue("At least one product id is required");
            }
            const { data } = await axios.post("/api/v1/bill-of-materials/product-ui/bulk", {
                productIds: ids,
                components,
                mode,
            });
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const deleteAllProductBom = createAsyncThunk(
    "bomUi/deleteAllProductBom",
    async (productId, { rejectWithValue }) => {
        try {
            const id = String(productId || "").trim();
            if (!id) {
                return rejectWithValue("Invalid product id");
            }
            const { data } = await axios.delete(`/api/v1/bill-of-materials/product-ui/${id}/components`);
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const fetchBomComponentLibrary = createAsyncThunk(
    "bomUi/fetchBomComponentLibrary",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get("/api/v1/bill-of-materials/product-components");
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const saveBomComponentLibraryItem = createAsyncThunk(
    "bomUi/saveBomComponentLibraryItem",
    async ({ id, component, description, qty, cost, notes, vendorId }, { rejectWithValue }) => {
        try {
            const v =
                vendorId !== undefined && vendorId !== null && String(vendorId).trim() !== ""
                    ? String(vendorId).trim()
                    : null;
            const { data } = await axios.put(`/api/v1/bill-of-materials/product-components/${id}`, {
                component,
                description,
                qty,
                cost,
                notes,
                vendorId: v,
            });
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const createBomComponentLibraryItem = createAsyncThunk(
    "bomUi/createBomComponentLibraryItem",
    async ({ component, description, qty, cost, notes, vendorId }, { rejectWithValue }) => {
        try {
            const v =
                vendorId !== undefined && vendorId !== null && String(vendorId).trim() !== ""
                    ? String(vendorId).trim()
                    : null;
            const { data } = await axios.post("/api/v1/bill-of-materials/product-components", {
                component,
                description,
                qty,
                cost,
                notes,
                vendorId: v,
            });
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

export const deleteBomComponentLibraryItem = createAsyncThunk(
    "bomUi/deleteBomComponentLibraryItem",
    async ({ id }, { rejectWithValue }) => {
        try {
            const { data } = await axios.delete(`/api/v1/bill-of-materials/product-components/${id}`);
            return data;
        } catch (error) {
            if ("string" === typeof error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error?.response?.data?.errors || error?.response?.data?.error || error.message);
        }
    }
);

function getListKey(tabId) {
    switch (tabId) {
        case "product":
            return "products";
        case "bundle":
            return "bundles";
        case "packaging":
            return "packaging";
        case "supplies":
            return "supplies";
        default:
            return null;
    }
}

function uniqueStrings(items) {
    return Array.from(new Set(ensureArray(items).filter(Boolean)));
}

const bomUiSlice = createSlice({
    name: "bomUi",
    initialState,
    reducers: {
        setActiveBomTab(state, action) {
            if (TAB_IDS.includes(action.payload)) {
                state.activeTab = action.payload;
            }
        },
        setBomSettingsOpen(state, action) {
            const payload = action.payload;
            if (typeof payload === "boolean") {
                state.settingsOpen = payload;
                if (!payload) {
                    state.settingsSection = "components";
                    state.settingsExpandedBoxGroupIds = [];
                    state.settingsExpandedInstructionGroupIds = [];
                }
                return;
            }
            state.settingsOpen = payload?.open !== undefined ? Boolean(payload.open) : true;
            if (!state.settingsOpen) {
                state.settingsSection = "components";
                state.settingsExpandedBoxGroupIds = [];
                state.settingsExpandedInstructionGroupIds = [];
                return;
            }
            if (payload?.section) {
                state.settingsSection = payload.section;
            }
            if ("expandedBoxGroupIds" in (payload || {})) {
                state.settingsExpandedBoxGroupIds = uniqueStrings(
                    ensureArray(payload?.expandedBoxGroupIds).map((id) => String(id))
                );
            }
            if ("expandedInstructionGroupIds" in (payload || {})) {
                state.settingsExpandedInstructionGroupIds = uniqueStrings(
                    ensureArray(payload?.expandedInstructionGroupIds).map((id) => String(id))
                );
            }
        },
        setBomChangeHistoryOpen(state, action) {
            state.changeHistoryOpen = Boolean(action.payload);
        },
        setBomFilter(state, action) {
            const { tab, key, value } = action.payload || {};
            if (!TAB_IDS.includes(tab)) return;
            if (!(key in state.filtersByTab[tab])) return;
            state.filtersByTab[tab][key] = value;
        },
        clearBomFilters(state, action) {
            const tab = action.payload;
            if (!TAB_IDS.includes(tab)) return;
            state.filtersByTab[tab] = createEmptyFilters();
        },
        toggleBomRowSelection(state, action) {
            const { tab, id } = action.payload || {};
            if (!TAB_IDS.includes(tab) || !id) return;
            const current = new Set(state.selectedIdsByTab[tab]);
            if (current.has(id)) {
                current.delete(id);
            } else {
                current.add(id);
            }
            state.selectedIdsByTab[tab] = Array.from(current);
        },
        setBomRowSelection(state, action) {
            const { tab, ids } = action.payload || {};
            if (!TAB_IDS.includes(tab)) return;
            state.selectedIdsByTab[tab] = uniqueStrings(ids);
        },
        clearBomRowSelection(state, action) {
            const tab = action.payload;
            if (!TAB_IDS.includes(tab)) return;
            state.selectedIdsByTab[tab] = [];
        },
        toggleBomExpandedRow(state, action) {
            const { tab, id } = action.payload || {};
            if (!["product", "bundle", "packaging"].includes(tab) || !id) return;
            const current = new Set(state.expandedIdsByTab[tab]);
            if (current.has(id)) {
                current.delete(id);
            } else {
                current.add(id);
            }
            state.expandedIdsByTab[tab] = Array.from(current);
        },
        setBomExpandedRows(state, action) {
            const { tab, ids } = action.payload || {};
            if (!["product", "bundle", "packaging"].includes(tab)) return;
            state.expandedIdsByTab[tab] = uniqueStrings(ids);
        },
        addBomProduct(state, action) {
            state.data.products.unshift(action.payload);
        },
        updateBomProduct(state, action) {
            const nextItem = action.payload;
            state.data.products = state.data.products.map((item) =>
                item.id === nextItem.id ? nextItem : item
            );
        },
        deleteBomProducts(state, action) {
            const ids = new Set(ensureArray(action.payload));
            state.data.products = state.data.products.filter((item) => !ids.has(item.id));
            state.selectedIdsByTab.product = state.selectedIdsByTab.product.filter((id) => !ids.has(id));
            state.expandedIdsByTab.product = state.expandedIdsByTab.product.filter((id) => !ids.has(id));
        },
        addBomBundle(state, action) {
            state.data.bundles.unshift(action.payload);
        },
        updateBomBundle(state, action) {
            const nextItem = action.payload;
            state.data.bundles = state.data.bundles.map((item) =>
                item.id === nextItem.id ? nextItem : item
            );
        },
        deleteBomBundles(state, action) {
            const ids = new Set(ensureArray(action.payload));
            state.data.bundles = state.data.bundles.filter((item) => !ids.has(item.id));
            state.selectedIdsByTab.bundle = state.selectedIdsByTab.bundle.filter((id) => !ids.has(id));
            state.expandedIdsByTab.bundle = state.expandedIdsByTab.bundle.filter((id) => !ids.has(id));
        },
        addBomPackaging(state, action) {
            state.data.packaging.unshift(action.payload);
        },
        updateBomPackaging(state, action) {
            const nextItem = action.payload;
            state.data.packaging = state.data.packaging.map((item) =>
                item.id === nextItem.id ? nextItem : item
            );
        },
        deleteBomPackaging(state, action) {
            const ids = new Set(ensureArray(action.payload));
            state.data.packaging = state.data.packaging.filter((item) => !ids.has(item.id));
            state.selectedIdsByTab.packaging = state.selectedIdsByTab.packaging.filter((id) => !ids.has(id));
            state.expandedIdsByTab.packaging = state.expandedIdsByTab.packaging.filter((id) => !ids.has(id));
        },
        addBomSupply(state, action) {
            state.data.supplies.unshift(action.payload);
        },
        updateBomSupply(state, action) {
            const nextItem = action.payload;
            state.data.supplies = state.data.supplies.map((item) =>
                item.id === nextItem.id ? nextItem : item
            );
        },
        deleteBomSupplies(state, action) {
            const ids = new Set(ensureArray(action.payload));
            state.data.supplies = state.data.supplies.filter((item) => !ids.has(item.id));
            state.selectedIdsByTab.supplies = state.selectedIdsByTab.supplies.filter((id) => !ids.has(id));
        },
        setBomCollection(state, action) {
            const { key, items } = action.payload || {};
            if (!(key in state.data)) return;
            state.data[key] = ensureArray(items);
        },
        addBomComponentLibraryItem(state, action) {
            state.data.componentLibrary.unshift(action.payload);
        },
        updateBomComponentLibraryItem(state, action) {
            const nextItem = action.payload;
            state.data.componentLibrary = state.data.componentLibrary.map((item) =>
                item.id === nextItem.id ? nextItem : item
            );
        },
        deleteBomComponentLibraryItems(state, action) {
            const ids = new Set(ensureArray(action.payload));
            state.data.componentLibrary = state.data.componentLibrary.filter((item) => !ids.has(item.id));
        },
        addBomBoxPreset(state, action) {
            state.data.boxPresets.unshift(action.payload);
        },
        updateBomBoxPreset(state, action) {
            const nextItem = action.payload;
            state.data.boxPresets = state.data.boxPresets.map((item) =>
                item.id === nextItem.id ? nextItem : item
            );
        },
        deleteBomBoxPresets(state, action) {
            const ids = new Set(ensureArray(action.payload));
            state.data.boxPresets = state.data.boxPresets.filter((item) => !ids.has(item.id));
        },
        addBomInstructionPreset(state, action) {
            state.data.instructionPresets.unshift(action.payload);
        },
        updateBomInstructionPreset(state, action) {
            const nextItem = action.payload;
            state.data.instructionPresets = state.data.instructionPresets.map((item) =>
                item.id === nextItem.id ? nextItem : item
            );
        },
        deleteBomInstructionPresets(state, action) {
            const ids = new Set(ensureArray(action.payload));
            state.data.instructionPresets = state.data.instructionPresets.filter((item) => !ids.has(item.id));
        },
        hydrateBomUiState(state, action) {
            const next = action.payload || {};
            state.data.products = ensureArray(next.products);
            state.data.bundles = ensureArray(next.bundles);
            state.data.packaging = ensureArray(next.packaging);
            state.data.supplies = ensureArray(next.supplies);
            state.data.componentLibrary = ensureArray(next.componentLibrary);
            state.data.boxPresets = ensureArray(next.boxPresets);
            state.data.packagingBoxPresetOptions = ensureArray(next.packagingBoxPresetOptions);
            state.data.packagingInstructionPresetOptions = ensureArray(next.packagingInstructionPresetOptions);
            state.data.instructionPresets = ensureArray(next.instructionPresets);
            state.data.supplyCategories = ensureArray(next.supplyCategories);
        },
        resetBomUiState() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(upsertBomSupply.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.upsertSupply];
            })
            .addCase(upsertBomSupply.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.upsertSupply, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
            })
            .addCase(upsertBomSupply.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.upsertSupply, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(deleteBomSupply.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.deleteSupply];
            })
            .addCase(deleteBomSupply.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deleteSupply, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
            })
            .addCase(deleteBomSupply.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deleteSupply, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(createSupplierCategory.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.createSupplierCategory];
            })
            .addCase(createSupplierCategory.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.createSupplierCategory, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
            })
            .addCase(createSupplierCategory.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.createSupplierCategory, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(deleteSupplierCategory.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.deleteSupplierCategory];
            })
            .addCase(deleteSupplierCategory.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deleteSupplierCategory, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
            })
            .addCase(deleteSupplierCategory.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deleteSupplierCategory, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(searchBomProducts.pending, (state) => {
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.searchProducts];
            })
            .addCase(searchBomProducts.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.searchProducts, state.loadingKeys);
                state.error = null;
            })
            .addCase(searchBomProducts.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.searchProducts, state.loadingKeys);
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(searchBomProductGroups.pending, (state) => {
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.searchProductGroups];
            })
            .addCase(searchBomProductGroups.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.searchProductGroups, state.loadingKeys);
                state.error = null;
            })
            .addCase(searchBomProductGroups.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.searchProductGroups, state.loadingKeys);
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(savePackagingBom.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.savePackagingBom];
            })
            .addCase(savePackagingBom.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.savePackagingBom, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
            })
            .addCase(savePackagingBom.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.savePackagingBom, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(deletePackagingBom.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.deletePackagingBom];
            })
            .addCase(deletePackagingBom.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deletePackagingBom, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
            })
            .addCase(deletePackagingBom.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deletePackagingBom, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(savePackagingInstructions.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.savePackagingInstructions];
            })
            .addCase(savePackagingInstructions.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.savePackagingInstructions, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
            })
            .addCase(savePackagingInstructions.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.savePackagingInstructions, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(deletePackagingInstructions.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.deletePackagingInstructions];
            })
            .addCase(deletePackagingInstructions.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deletePackagingInstructions, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
            })
            .addCase(deletePackagingInstructions.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deletePackagingInstructions, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(fetchPackagingBomUi.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.fetchPackagingUi];
            })
            .addCase(fetchPackagingBomUi.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.fetchPackagingUi, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
                const p = action.payload || {};
                state.data.packaging = ensureArray(p.packaging);
                state.data.packagingBoxPresetOptions = ensureArray(p.packagingBoxPresetOptions);
                state.data.packagingInstructionPresetOptions = ensureArray(p.packagingInstructionPresetOptions);
            })
            .addCase(fetchPackagingBomUi.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.fetchPackagingUi, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(fetchBundleBomUi.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.fetchBundleUi];
            })
            .addCase(fetchBundleBomUi.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.fetchBundleUi, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
                const p = action.payload || {};
                state.data.bundles = ensureArray(p.bundles);
            })
            .addCase(fetchBundleBomUi.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.fetchBundleUi, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(fetchSuppliesBomUi.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.getSupplies];
            })
            .addCase(fetchSuppliesBomUi.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.getSupplies, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
                const p = action.payload || {};
                state.data.supplies = ensureArray(p.supplies);
                state.data.supplyCategories = ensureArray(p.supplyCategories);
            })
            .addCase(fetchSuppliesBomUi.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.getSupplies, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(fetchProductBomUi.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.fetchProductUi];
            })
            .addCase(fetchProductBomUi.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.fetchProductUi, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
                const p = action.payload || {};
                state.data.products = ensureArray(p.products);
            })
            .addCase(fetchProductBomUi.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.fetchProductUi, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(saveProductBom.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.saveProductBom];
            })
            .addCase(saveProductBom.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.saveProductBom, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
            })
            .addCase(saveProductBom.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.saveProductBom, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(saveProductBomBulk.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.saveProductBomBulk];
            })
            .addCase(saveProductBomBulk.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.saveProductBomBulk, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
            })
            .addCase(saveProductBomBulk.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.saveProductBomBulk, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(getBomChangeLog.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.getBomChangeLog];
            })
            .addCase(getBomChangeLog.fulfilled, (state, action) => {
                state.bomChangeLog = action.payload?.items || [];
                state.bomChangeLogPagination = {
                    currentPage: action.payload?.pagination?.currentPage || 1,
                    totalPages: action.payload?.pagination?.totalPages || 1,
                    totalCount: action.payload?.pagination?.totalCount || 0,
                    limit: action.payload?.pagination?.limit || 100,
                    isTheLastPage: Boolean(action.payload?.pagination?.isTheLastPage),
                };
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.getBomChangeLog, state.loadingKeys);
                state.error = null;
            })
            .addCase(getBomChangeLog.rejected, (state, action) => {
                state.error = action.payload || action.error?.message || null;
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.getBomChangeLog, state.loadingKeys);
            })
            .addCase(rollbackBomChanges.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.rollbackBomChanges];
            })
            .addCase(rollbackBomChanges.fulfilled, (state) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.rollbackBomChanges, state.loadingKeys);
                state.error = null;
            })
            .addCase(rollbackBomChanges.rejected, (state, action) => {
                state.error = action.payload || action.error?.message || null;
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.rollbackBomChanges, state.loadingKeys);
            })
            .addCase(deleteAllProductBom.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.deleteAllProductBom];
            })
            .addCase(deleteAllProductBom.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deleteAllProductBom, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
                const pid = action.payload?.product_id;
                if (!pid) return;
                const ids = new Set([String(pid)]);
                state.data.products = state.data.products.filter((item) => !ids.has(item.id));
                state.selectedIdsByTab.product = state.selectedIdsByTab.product.filter((id) => !ids.has(id));
                state.expandedIdsByTab.product = state.expandedIdsByTab.product.filter((id) => !ids.has(id));
            })
            .addCase(deleteAllProductBom.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deleteAllProductBom, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(fetchBomComponentLibrary.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.fetchComponentLibrary];
            })
            .addCase(fetchBomComponentLibrary.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.fetchComponentLibrary, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
                const p = action.payload || {};
                state.data.componentLibrary = ensureArray(p.items);
            })
            .addCase(fetchBomComponentLibrary.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.fetchComponentLibrary, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(saveBomComponentLibraryItem.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.saveComponentLibraryItem];
            })
            .addCase(saveBomComponentLibraryItem.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.saveComponentLibraryItem, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
                const updated = action.payload?.item;
                if (updated?.id != null) {
                    const uid = Number(updated.id);
                    const idx = state.data.componentLibrary.findIndex((item) => Number(item?.id) === uid);
                    if (idx >= 0) {
                        state.data.componentLibrary[idx] = {
                            ...state.data.componentLibrary[idx],
                            ...updated,
                        };
                    } else {
                        state.data.componentLibrary.push(updated);
                    }
                }
            })
            .addCase(saveBomComponentLibraryItem.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.saveComponentLibraryItem, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(createBomComponentLibraryItem.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.saveComponentLibraryItem];
            })
            .addCase(createBomComponentLibraryItem.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.saveComponentLibraryItem, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
                const created = action.payload?.item;
                if (created?.id != null) {
                    const uid = Number(created.id);
                    const idx = state.data.componentLibrary.findIndex((item) => Number(item?.id) === uid);
                    if (idx >= 0) {
                        state.data.componentLibrary[idx] = {
                            ...state.data.componentLibrary[idx],
                            ...created,
                        };
                    } else {
                        state.data.componentLibrary.unshift(created);
                    }
                }
            })
            .addCase(createBomComponentLibraryItem.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.saveComponentLibraryItem, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(deleteBomComponentLibraryItem.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, BomUiLoadingKeys.deleteComponentLibraryItem];
            })
            .addCase(deleteBomComponentLibraryItem.fulfilled, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deleteComponentLibraryItem, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = null;
                const deletedId = Number(action.payload?.id);
                if (Number.isFinite(deletedId)) {
                    state.data.componentLibrary = state.data.componentLibrary.filter(
                        (item) => Number(item?.id) !== deletedId
                    );
                }
            })
            .addCase(deleteBomComponentLibraryItem.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(BomUiLoadingKeys.deleteComponentLibraryItem, state.loadingKeys);
                state.loading = state.loadingKeys.length > 0;
                state.error = action.payload || action.error?.message || null;
            });
    },
});

export const {
    setActiveBomTab,
    setBomSettingsOpen,
    setBomChangeHistoryOpen,
    setBomFilter,
    clearBomFilters,
    toggleBomRowSelection,
    setBomRowSelection,
    clearBomRowSelection,
    toggleBomExpandedRow,
    setBomExpandedRows,
    addBomProduct,
    updateBomProduct,
    deleteBomProducts,
    addBomBundle,
    updateBomBundle,
    deleteBomBundles,
    addBomPackaging,
    updateBomPackaging,
    deleteBomPackaging,
    addBomSupply,
    updateBomSupply,
    deleteBomSupplies,
    setBomCollection,
    addBomComponentLibraryItem,
    updateBomComponentLibraryItem,
    deleteBomComponentLibraryItems,
    addBomBoxPreset,
    updateBomBoxPreset,
    deleteBomBoxPresets,
    addBomInstructionPreset,
    updateBomInstructionPreset,
    deleteBomInstructionPresets,
    hydrateBomUiState,
    resetBomUiState,
} = bomUiSlice.actions;

export const selectBomUi = (state) => state.bomUi || initialState;

export const selectBomTabFilters = (state, tab) =>
    selectBomUi(state).filtersByTab[tab] || createEmptyFilters();

export const selectBomTabSelection = (state, tab) =>
    ensureArray(selectBomUi(state).selectedIdsByTab[tab]);

export const selectBomTabExpanded = (state, tab) =>
    ensureArray(selectBomUi(state).expandedIdsByTab[tab]);

export const selectBomTabItems = (state, tab) => {
    const listKey = getListKey(tab);
    if (!listKey) return [];
    return ensureArray(selectBomUi(state).data[listKey]);
};

export default bomUiSlice.reducer;
